import React, { useState } from "react";
import useFetch from "@/lib/useFetch";
import { GlassCard, PageTitle, StatusBadge, EmptyState, MetricCard } from "@/components/lux";
import api, { apiError, money } from "@/lib/api";
import { toast } from "sonner";
import { ArrowDownToLine, Check, X, Clock, Coins } from "lucide-react";

const STATUSES = ["all", "requested", "paid", "rejected"];

export default function AdminPoolWithdrawals() {
  const [status, setStatus] = useState("requested");
  const [pool, setPool] = useState("all");
  const { data, loading, reload } = useFetch(`/admin/pool-withdrawals?status=${status}${pool !== "all" ? `&pool_id=${pool}` : ""}`, [status, pool]);
  const [busy, setBusy] = useState(null);

  const wds = data?.withdrawals || [];

  const act = async (id, action) => {
    setBusy(id + action);
    try {
      const r = await api.post(`/admin/pool-withdrawals/${id}/action`, { action });
      toast.success(r.data.message);
      reload();
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Failed"); }
    finally { setBusy(null); }
  };

  return (
    <div className="space-y-6" data-testid="admin-pool-withdrawals">
      <PageTitle title="Pool Payouts" subtitle="Approve or reject Global Pool withdrawal requests. Reject refunds the amount to the member's pool balance." />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <MetricCard label="Pending Requests" value={data?.pending_count ?? 0} icon={Clock} />
        <MetricCard label="Pending Net Payout" value={`$${money(data?.pending_net || 0)}`} icon={Coins} accent />
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {STATUSES.map((s) => (
          <button key={s} onClick={() => setStatus(s)} className={`btn3d ${status === s ? "btn3d-gold" : "btn3d-dark"} px-3 py-1.5 text-xs capitalize`} data-testid={`pool-wd-filter-${s}`}>{s}</button>
        ))}
        <select value={pool} onChange={(e) => setPool(e.target.value)} className="rounded-lg border border-white/10 bg-white/5 px-3 py-1.5 text-xs text-white outline-none focus:border-[var(--gold)]/50" data-testid="pool-wd-pool-filter">
          <option value="all">All Pools</option>
          {[1, 2, 3, 4, 5, 6, 7].map((n) => <option key={n} value={n}>Pool {n}</option>)}
        </select>
      </div>
      <GlassCard className="p-6">
        {loading ? <div className="skeleton h-40" /> : wds.length ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-[var(--text-dim)]"><tr className="border-b border-white/10">
                <th className="py-3">ID</th><th className="py-3">Member</th><th className="py-3">Pool</th><th className="py-3">Amount</th><th className="py-3">Fee</th><th className="py-3">Net</th><th className="py-3">Wallet</th><th className="py-3">Status</th><th className="py-3">Date</th><th className="py-3">Action</th></tr></thead>
              <tbody>
                {wds.map((w) => (
                  <tr key={w.pw_id} className="row-hover border-b border-white/5" data-testid={`pool-wd-row-${w.pw_id}`}>
                    <td className="py-3 text-white">{w.pw_id}</td>
                    <td className="py-3">{w.member_id}</td>
                    <td className="py-3">Pool {w.pool_id}</td>
                    <td className="py-3">${money(w.amount)}</td>
                    <td className="py-3 text-[var(--maroon)]">-${money(w.fee)}</td>
                    <td className="py-3 gold-text">${money(w.net)}</td>
                    <td className="py-3 max-w-[130px] truncate text-[var(--text-dim)]" title={w.wallet_address}>{w.wallet_address}</td>
                    <td className="py-3"><StatusBadge status={w.status} /></td>
                    <td className="py-3 text-[var(--text-dim)]">{(w.created_at || "").slice(0, 10)}</td>
                    <td className="py-3">
                      {w.status === "requested" ? (
                        <div className="flex gap-2">
                          <button onClick={() => act(w.pw_id, "paid")} disabled={busy === w.pw_id + "paid"} className="flex items-center gap-1 rounded-lg btn3d-gold px-2.5 py-1.5 text-xs font-bold text-black disabled:opacity-50" data-testid={`pool-wd-pay-${w.pw_id}`}><Check size={13} /> Pay</button>
                          <button onClick={() => act(w.pw_id, "reject")} disabled={busy === w.pw_id + "reject"} className="flex items-center gap-1 rounded-lg btn3d btn3d-dark px-2.5 py-1.5 text-xs disabled:opacity-50" data-testid={`pool-wd-reject-${w.pw_id}`}><X size={13} /> Reject</button>
                        </div>
                      ) : <span className="text-xs text-[var(--text-dim)]">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <EmptyState icon={ArrowDownToLine} title="No pool withdrawals" />}
      </GlassCard>
    </div>
  );
}
